import { styled } from '@linaria/react';
import { useState } from 'react';
import { type CapabilityGroupTool, CapabilityMode } from '../../../../api/agents-client/agent-client.type';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { IconTile } from '../shared/settings/icon-tile';
import { ExpandChevron, ExpandButton as SharedExpandButton } from '../shared/settings/settings-card';
import { getGroupVisual } from './agent-customize.constants';
import { getEnableGroupState } from './agent-customize.helpers';

interface CapabilityGroupRowProps {
  kind: 'read' | 'write';
  mode: CapabilityMode;
  tools: CapabilityGroupTool[];
  enabledTools: Record<string, boolean>;
  /** Per-tool approval-required state for approval-mode write tools. */
  approvals?: Record<string, boolean>;
  /** Updates one approval-mode write tool without changing its siblings. */
  onApprovalChange?: (name: string, approvalRequired: boolean) => void;
  /** Dims the tool list when the parent capability is fully off (enable mode). */
  parentDisabled?: boolean;
}

type ApprovalOptionKind = 'allow' | 'approval';

/**
 * Resolves the resting background for an approval option.
 *
 * @param {boolean} selected - Whether the option is currently selected
 * @param {ApprovalOptionKind} kind - The approval option being rendered
 * @returns {string} The design-token color for the option background
 */
function getApprovalOptionBackground(selected: boolean, kind: ApprovalOptionKind): string {
  if (!selected) return 'transparent';
  return kind === 'allow' ? 'var(--primary-lightest)' : 'var(--base-white)';
}

/**
 * Resolves the hover background for an approval option.
 *
 * @param {boolean} selected - Whether the option is currently selected
 * @param {ApprovalOptionKind} kind - The approval option being rendered
 * @returns {string} The design-token color for the hovered option background
 */
function getApprovalOptionHoverBackground(selected: boolean, kind: ApprovalOptionKind): string {
  if (selected && kind === 'allow') return 'var(--primary-lighter)';
  return 'var(--sky-light)';
}

/**
 * A single read/write tool group inside a capability. Renders a collapsible
 * header (kind badge, label, count summary) over the per-tool rows.
 *
 * Enable-mode groups are informational because their controls live at the
 * capability level. Approval-mode write groups add a per-tool approval button,
 * while their capability dropdown remains the bulk control. Required tools
 * show an "Always on" pill.
 *
 * @param {CapabilityGroupRowProps} props - Group kind/mode, tools, state, and approval callback
 * @returns {JSX.Element} The group row
 */
export function CapabilityGroupRow({
  kind,
  mode,
  tools,
  enabledTools,
  approvals,
  onApprovalChange,
  parentDisabled = false,
}: CapabilityGroupRowProps) {
  const [expanded, setExpanded] = useState(true);
  const visual = getGroupVisual(kind);
  const isApproval = mode === CapabilityMode.Approval;
  const isApprovalRead = isApproval && kind === 'read';

  const enableState = getEnableGroupState(tools, enabledTools);

  let label: string;
  if (isApproval) {
    label = kind === 'read' ? 'Read' : 'Write & delete';
  } else {
    label = kind === 'read' ? 'Read' : 'Write';
  }

  let countLabel = '';
  if (isApprovalRead) {
    countLabel = 'Always on';
  } else if (!isApproval) {
    countLabel = `${enableState.onCount}/${enableState.total}`;
  }

  return (
    <Group>
      <Header>
        <ExpandButton
          type="button"
          aria-expanded={expanded}
          aria-label={`${label} tools`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <ExpandChevron expanded={expanded} size={14} color="var(--ink-lighter)" />
          <IconTile background={visual.badgeBg} size="xs">
            <Icon name={visual.icon} size={13} color={visual.color} />
          </IconTile>
          <Text size="small" weight="medium" color="var(--ink-dark)">
            {label}
          </Text>
          {countLabel && (
            <Text size="tiny" color="var(--ink-lighter)">
              {countLabel}
            </Text>
          )}
        </ExpandButton>
      </Header>

      {expanded && (
        <ToolList $disabled={mode === CapabilityMode.Enable && parentDisabled}>
          {tools.map((tool) => {
            const isLocked = tool.required;
            const showsApprovalControl = isApproval && kind === 'write' && approvals && onApprovalChange;
            const approvalRequired = Boolean(approvals?.[tool.name]);
            return (
              <ToolRow key={tool.name}>
                <ToolInfo>
                  <Text size="small" weight="medium" color="var(--ink-darkest)">
                    {tool.label}
                  </Text>
                  <Text size="tiny" color="var(--ink-lighter)">
                    {tool.description}
                  </Text>
                </ToolInfo>
                {isLocked && (
                  <LockPill title="Always available — cannot be disabled">
                    <Icon name="lock" size={12} color="var(--ink-light)" />
                    <Text size="tiny" weight="medium" color="var(--ink-light)">
                      Always on
                    </Text>
                  </LockPill>
                )}
                {showsApprovalControl && (
                  <ApprovalControl role="group" aria-label={`${tool.label} approval setting`}>
                    <Tooltip variant="black" label="Always allow">
                      <ApprovalOptionButton
                        type="button"
                        $selected={!approvalRequired}
                        $kind="allow"
                        aria-pressed={!approvalRequired}
                        aria-label={`${tool.label}: Always allow`}
                        onClick={() => onApprovalChange(tool.name, false)}
                      >
                        <Icon
                          name="check"
                          size={17}
                          color={!approvalRequired ? 'var(--primary-darkest)' : 'var(--ink-light)'}
                          pointer
                        />
                      </ApprovalOptionButton>
                    </Tooltip>
                    <Tooltip variant="black" label="Approval required">
                      <ApprovalOptionButton
                        type="button"
                        $selected={approvalRequired}
                        $kind="approval"
                        aria-pressed={approvalRequired}
                        aria-label={`${tool.label}: Approval required`}
                        onClick={() => onApprovalChange(tool.name, true)}
                      >
                        <Icon
                          name="pan_tool"
                          size={16}
                          color={approvalRequired ? 'var(--ink-dark)' : 'var(--ink-light)'}
                          pointer
                        />
                      </ApprovalOptionButton>
                    </Tooltip>
                  </ApprovalControl>
                )}
              </ToolRow>
            );
          })}
        </ToolList>
      )}
    </Group>
  );
}

const Group = styled.div`
  border-top: 1px dashed var(--sky-light);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
`;

const ExpandButton = styled(SharedExpandButton)`
  gap: 10px;
`;

const ToolList = styled.div<{ $disabled: boolean }>`
  padding-bottom: 6px;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const ToolRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 8px 32px;
`;

const ToolInfo = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.4;
`;

const LockPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  background: var(--sky-lighter);
  flex-shrink: 0;
`;

const ApprovalControl = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 2px;
  border-radius: 9px;
  background: var(--sky-lighter);
  flex-shrink: 0;
`;

const ApprovalOptionButton = styled.button<{ $selected: boolean; $kind: ApprovalOptionKind }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: ${({ $selected, $kind }) => getApprovalOptionBackground($selected, $kind)};
  cursor: pointer;

  &:hover {
    background: ${({ $selected, $kind }) => getApprovalOptionHoverBackground($selected, $kind)};
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;
